import { useState } from 'react';
import type { AccountLinkIssue } from '@bekebi/contracts/api';
import { Button } from '../../components/button';
import { formatDateTime } from '../services/format';

const WHAT: Record<AccountLinkIssue['purpose'], string> = {
  invitation: 'invitation',
  password_reset: 'password reset link',
};

/**
 * What happened to a new invitation or reset link. When the mail server accepted the email, only
 * that is said. When it did not (or email is not set up), the link is shown here once, so the Admin
 * can pass it on privately; it is not stored and cannot be shown again.
 */
export function LinkIssueResult({ email, result }: { email: string; result: AccountLinkIssue }) {
  const [copied, setCopied] = useState(false);
  const what = WHAT[result.purpose];
  const until = formatDateTime(result.expiresAt);

  if (result.email === 'accepted' || !result.setupLink) {
    return (
      <p role="status" className="rounded-md bg-success-soft p-3 text-sm text-success">
        The {what} was emailed to {email} (accepted by the mail server). The link works once, until{' '}
        {until}.
      </p>
    );
  }

  const link = result.setupLink;
  return (
    <div role="alert" className="rounded-md border border-warning-line bg-warning-soft p-3 text-sm">
      <p className="font-medium text-warning">
        {result.email === 'not_set_up'
          ? `Email is not set up on the server yet, so the ${what} was not sent.`
          : `The mail server did not accept the ${what} email.`}
      </p>
      <p className="mt-2 text-fg-2">
        Give {email} this link yourself, privately (for example in a direct message, never in a
        group). It works once, until {until}. It is shown only now.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <input
          readOnly
          value={link}
          aria-label={`Link for ${email}`}
          onFocus={(e) => e.currentTarget.select()}
          className="min-h-10 min-w-0 flex-1 rounded-md border border-line-strong bg-surface px-3 font-mono text-xs"
        />
        <Button
          variant="secondary"
          onClick={() =>
            void navigator.clipboard
              ?.writeText(link)
              .then(() => setCopied(true))
              .catch(() => setCopied(false))
          }
        >
          {copied ? 'Copied' : 'Copy link'}
        </Button>
      </div>
    </div>
  );
}
